import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  alpha,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton
} from "@mui/material";

import { CustomCard } from "../../components/CustomCard";
import { useTheme } from "../../style/theme";
import ApexChart2 from "../../components/ui/ApexChart2";
import BarChart3 from "../../components/ui/BarChart3";
import { useTheme as themee } from "@mui/material";
import { useSubscriptionAnalytics, useSubscriptions } from "../../hooks/useSubscriptions";
import SubscriptionStatusChart from "./SubscriptionStatusChart";
import RevenueTrendChart from "./RevenueTrendChart";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SubscriptionOverview = () => {
  const navigate = useNavigate();
  const [growthYear, setGrowthYear] = useState('all');
  const [growthMonth, setGrowthMonth] = useState('all');
  const [revenueYear, setRevenueYear] = useState('all');
  const [revenueMonth, setRevenueMonth] = useState('all');
  
  const currentYear = new Date().getFullYear();

  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useSubscriptionAnalytics();
  
  const analytics = analyticsData?.data || {};

  const { data: recentSubscriptions, isLoading: subscriptionsLoading, error: subscriptionsError } = useSubscriptions({
    page: 1,
    limit: 8,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const isLoading = analyticsLoading || subscriptionsLoading;
  const error = analyticsError || subscriptionsError;
  const { mode } = useTheme();
  const theme = themee();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const yearsAvailable = analytics.series?.createdYearly?.map(y => y.year) || [];

  // Format analytics data for cards
  const cardData = analytics ? [
    {
      title: "Total Subscriptions",
      value: analytics.totalSubscriptions?.toLocaleString() || "0",
      type: (analytics.totalSubscriptionsGrowth || 0) >= 0 ? "increase" : "decrease",
      percentage: Math.abs(analytics.totalSubscriptionsGrowth || 0).toFixed(1),
    },
    {
      title: "Paid Active Subscriptions",
      value: analytics.paidActiveSubscriptions?.toLocaleString() || "0",
      type: "neutral",
      percentage: null,
    },
    {
      title: "Free Active Subscriptions",
      value: analytics.freeActiveSubscriptions?.toLocaleString() || "0",
      type: "neutral",
      percentage: null,
    },
    {
      title: "Cancelled Subscriptions",
      value: analytics.cancelledSubscriptions?.toLocaleString() || "0",
      type: "neutral",
      percentage: null,
    },
    {
      title: "Monthly Recurring Revenue (MRR)",
      value: `$${(analytics.mrr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      type: "neutral",
      percentage: null,
    },
    {
      title: "Annual Recurring Revenue (ARR)",
      value: `$${(analytics.arr || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      type: "neutral",
      percentage: null,
    },
    {
      title: "Average Revenue Per User (ARPU)",
      value: `$${(analytics.arpu || 0).toFixed(2)}`,
      type: "neutral",
      percentage: null,
    },
    {
      title: "New Subscriptions",
      value: analytics.newSubscriptionsThisMonth?.toLocaleString() || "0",
      type: (analytics.newSubscriptionsGrowth || 0) >= 0 ? "increase" : "decrease",
      percentage: Math.abs(analytics.newSubscriptionsGrowth || 0).toFixed(1),
    },
  ] : [];

  // Format recent subscriptions data
  const subscriptions = recentSubscriptions?.data?.slice(0, 8).map(sub => ({
    name: sub.user?.name || sub.userId || "Unknown User",
    date: new Date(sub.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }),
    price: `$${(parseFloat(sub.amount) || 0).toFixed(2)}`,
    Icon: <PersonIcon />,
    userId: sub.userId || sub.user?._id,
  })) || [];

  if (isLoading) {
    return (
      <Box sx={{ backgroundColor: "background.default" }} className="flex items-center justify-center h-96">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ backgroundColor: "background.default" }} className="p-4">
        <Alert severity="error">
          Failed to load subscription data: {error.response?.data?.message || error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "background.default"}} className="flex flex-col gap-6">
      {/* Stats Cards */}
      <Box>
        <CustomCard
          data={cardData}
          mode={mode}
          length={cardData.length}
        />
      </Box>

     

      {/* Revenue and Top Products Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Revenue Trends
              </Typography>
            </Box>
            <RevenueTrendChart />
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
              Top Products by Count
            </Typography>
            <BarChart3 limit={5} />
          </Box>
        </Grid>
      </Grid>

      {/* Subscription Growth */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.default',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Subscription Growth
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={growthYear}
                onChange={(e) => setGrowthYear(e.target.value)}
                variant="outlined"
              >
                <MenuItem value="all">All Years</MenuItem>
                {yearsAvailable.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={growthMonth}
                onChange={(e) => setGrowthMonth(e.target.value)}
                variant="outlined"
                disabled={growthYear === 'all'}
              >
                <MenuItem value="all">All Months</MenuItem>
                {months.map((m, idx) => (
                  <MenuItem key={idx} value={idx}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <ApexChart2 year={growthYear} month={growthMonth} />
      </Box>
       {/* Main Charts Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
              Recent Subscriptions
            </Typography>
            <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2, width: '100%', margin: 'auto' }}>
              {subscriptions.map((subscription, index) => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                    border: `1px solid ${theme.palette.divider}`,
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: mode === "dark" ? "neutral.700" : "neutral.200",
                        borderRadius: "50%"
                      }}
                    >
                      {subscription.Icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {subscription.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {subscription.date}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/users?userId=${subscription.userId}&tab=subscriptions`)}
                      sx={{ color: theme.palette.primary.main }}
                      title="View User Details"
                    >
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {subscription.price}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
             Subscriptions Status Distribution
            </Typography>
            <SubscriptionStatusChart />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionOverview;